import { AccountStatus, ProgramType } from '@newfront-insurance/account-api';
import { useAccountSwitcherOptions, type Account } from '@newfront-insurance/admin-ui/accounts';
import { useCallback, useState } from 'react';

/**
 * Nova search account result type that matches the expected structure
 * for the SearchRow component.
 */
export interface NovaSearchAccountResult {
  uuid: string;
  label: string;
  badges?: string[];
  description?: string;
}

export type SelectedAccount = NovaSearchAccountResult | null | undefined;

interface UseAccountSearchReturn {
  handleSearch: (value: string) => Promise<void>;
  handleReset: () => void;
  isLoading: boolean;
  allResults: NovaSearchAccountResult[];
  results?: NovaSearchAccountResult[];
  error?: {
    statusText: string;
    data: {
      errorMessage: string;
    };
  };
}

interface UseAccountSearchProps {
  loggedInUserUuid: string;
  userScopes: string[];
}

const PROGRAM_TYPE_TO_BADGE: Record<ProgramType, string> = {
  [ProgramType.PROPERTY_AND_CASUALTY]: 'BI',
  [ProgramType.EMPLOYEE_BENEFITS]: 'TR',
};

/**
 * Converts an Account from admin-ui to NovaSearchAccountResult format.
 * Includes badges for program type and archived status.
 */
function accountToNovaSearchResult(account: Account): NovaSearchAccountResult {
  const badges: string[] = [];

  // Add archived badge if applicable
  if (account.status.status === AccountStatus.ARCHIVED) {
    badges.push('Archived');
  }

  // Add program type badge
  if (account.programType) {
    const programTypeBadge = PROGRAM_TYPE_TO_BADGE[account.programType];
    if (programTypeBadge) {
      badges.push(programTypeBadge);
    }
  }

  return {
    uuid: account.uuid,
    label: account.name,
    description: account.dba,
    badges: badges.length > 0 ? badges : undefined,
  };
}

/**
 * Hook to fetch and search accounts for the Nova account selector.
 *
 * This hook uses the same data fetching logic as the admin-ui account switcher,
 * ensuring proper ACL (Access Control List) handling:
 * - Users with specific roles can search ALL accounts
 * - Other users can only see accounts they are related to via servicing team membership
 *
 * @param loggedInUserUuid - The UUID of the currently logged-in user
 * @param userScopes - The scopes/roles of the currently logged-in user
 * @returns Object containing search handlers, loading state, results, and error state
 */
export function useAccountSearch({ loggedInUserUuid, userScopes }: UseAccountSearchProps): UseAccountSearchReturn {
  const [searchValue, setSearchValue] = useState<string>('');
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const { accounts, search, isLoading } = useAccountSwitcherOptions({
    loggedInUserUuid,
    userScopes,
  });

  const handleSearch = useCallback(
    async (value: string): Promise<void> => {
      setSearchValue(value);
      setHasSearched(true);
      search(value);
    },
    [search],
  );

  const handleReset = useCallback((): void => {
    setSearchValue('');
    setHasSearched(false);
    search('');
  }, [search]);

  // Only show results if user has searched
  const allResults = accounts.map(accountToNovaSearchResult);
  const results = hasSearched && searchValue.trim() ? allResults : undefined;

  return {
    handleSearch,
    handleReset,
    isLoading,
    allResults,
    results,
    error: undefined,
  };
}
